import { MidiState, MidiDevice } from './state';
import { MidiEvent } from './types';

interface MIDIMessageEvent extends Event {
  data: Uint8Array;
}

export class MidiManager {
  state = new MidiState();
  private midiAccess: MIDIAccess | null = null;
  private listeners: Set<(event: MidiEvent) => void> = new Set();

  constructor() {
    // Lazy init
  }

  private initializationPromise: Promise<void> | null = null;
  public async ensureInitialized() {
    if (this.initializationPromise) return this.initializationPromise;
    this.initializationPromise = this.init();
    return this.initializationPromise;
  }

  private async init() {
    if (!navigator.requestMIDIAccess) {
      console.warn('Web MIDI API not supported in this browser.');
      return;
    }

    try {
      this.midiAccess = await navigator.requestMIDIAccess();
      this.updateDevices();

      if (this.midiAccess) {
        this.midiAccess.onstatechange = () => {
          this.updateDevices();
        };
      }
    } catch (e) {
      console.error('Failed to access Web MIDI API:', e);
    }
  }

  public onMidiEvent(callback: (event: MidiEvent) => void, options?: { skipPermissionCheck?: boolean }): () => void {
    if (!options?.skipPermissionCheck) {
      this.ensureInitialized();
    }
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  private dispatchEvent(event: MidiEvent) {
    this.listeners.forEach(listener => listener(event));
  }

  private updateDevices() {
    if (!this.midiAccess) return;
    const inputs = Array.from(this.midiAccess.inputs.values());

    inputs.forEach((input: MIDIInput) => {
      const device: MidiDevice = {
        id: input.id,
        name: input.name || `Unknown Device ${input.id}`,
        manufacturer: input.manufacturer || '',
        state: input.state,
        connection: input.connection
      };

      this.state.addDevice(device);

      // Ensure we are listening
      input.onmidimessage = (e: any) => this.handleMidiMessage(e, input.id);
    });
  }

  private handleMidiMessage(event: MIDIMessageEvent, deviceId: string) {
    // Filter if specific devices are selected and this one isn't
    if (this.state.selectedDeviceIds.size > 0 && !this.state.selectedDeviceIds.has(deviceId)) {
      return;
    }

    const data = event.data;
    if (!data || data.length < 2) return;

    const status = data[0];
    const command = status >> 4;
    const channel = (status & 0xf) + 1; // 1-16
    const data1 = data[1];
    const data2 = data.length > 2 ? data[2] : 0;

    let midiEvent: MidiEvent | null = null;

    // Note On (0x9)
    if (command === 0x9) {
      const rawVelocity = data2;
      if (rawVelocity > 0) {
        midiEvent = {
          deviceId,
          channel,
          type: 'note_on',
          note: data1,
          velocity: rawVelocity / 127.0,
          time: Date.now()
        };
      } else {
        // Velocity 0 is Note Off
        midiEvent = {
          deviceId,
          channel,
          type: 'note_off',
          note: data1,
          velocity: 0,
          time: Date.now()
        };
      }
    }
    // Note Off (0x8)
    else if (command === 0x8) {
      midiEvent = {
        deviceId,
        channel,
        type: 'note_off',
        note: data1,
        velocity: 0,
        time: Date.now()
      };
    }
    // Control Change (0xB)
    else if (command === 0xB) {
      midiEvent = {
        deviceId,
        channel,
        type: 'cc',
        cc: data1,
        value: data2,
        time: Date.now()
      };
    }

    if (midiEvent) {
      this.state.addEvent(midiEvent);
      this.dispatchEvent(midiEvent);
    }
  }
}

export const midiManager = new MidiManager();
